import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HistoryBaseComponent } from 'src/app/history-base/history-base.component';
import { OrderStatus } from 'src/infrastructure/enums/orderStatus';
import { PaymentMethod } from 'src/infrastructure/enums/paymentMethod';
import { ReturnStatus } from 'src/infrastructure/enums/returnStatus';
import { CartLine } from 'src/models/DTO/cart/cartLine';
import { CreatedOrder } from 'src/models/DTO/orders/createdOrder';
import { Organisation } from 'src/models/DTO/organisation/organisation';
import { PaymentIntents } from 'src/models/DTO/paymentIntents/paymentIntents';
import { OrderReturnRequest } from 'src/models/DTO/returns/orderReturnRequest';
import { OrderReturnRequests } from 'src/models/DTO/returns/orderReturnRequests';
import { ToastService } from 'src/services/core/toast/toast.service';
import { ImagesService } from 'src/services/request/images/images.service';
import { OrderService } from 'src/services/request/orders/order.service';
import { PartnerService } from 'src/services/request/partner/partner.service';
import { OrderReturnServiceService } from 'src/services/request/returns/order-return-service.service';
import { StripeService } from 'src/services/request/stripe/stripe.service';
import { HistoryService } from 'src/services/state/history/history.service';

@Component({
  selector: 'app-returns',
  templateUrl: './returns.component.html',
  styleUrls: ['./returns.component.css']
})
export class ReturnsComponent extends HistoryBaseComponent implements OnInit {
  private _orders?: CreatedOrder[];
  private _paymentIntent?: PaymentIntents;
  private _organisation?: Organisation;
  private _orderReturnRequests: OrderReturnRequests = { data: [] };

  private _sorts = {
    newest: 'newest',
    oldest: 'oldest'
  };

  private _statuses = {
    confirmed: 'confirmed',
    cancelled: 'cancelled'
  };

  constructor(
    private orderReturnsService: OrderReturnServiceService,
    private partnerService: PartnerService,
    private ordersService: OrderService,
    private imagesService: ImagesService,
    private stripeService: StripeService,
    private toastrService: ToastService,
    private router: Router,
    protected location: Location,
    protected historyService: HistoryService
  ) {
    super(location, historyService);
    this.loadOrderReturns();
  }

  ngOnInit(): void {}

  loadOrderReturns(): void {
    this.orderReturnsService.getAllOrderReturns(this._sorts.newest).subscribe((returns) => {
      this._orderReturnRequests = returns;
      this._orderReturnRequests.data.forEach((request) => {
        this.loadOrder(request);
      });
    });
  }

  private loadOrder(returnRequest: OrderReturnRequest): void {
    const orderUUID = returnRequest.order.uuid;

    this.ordersService.getOrder(orderUUID).subscribe((order) => {
      returnRequest.fullOrder = order;
      this.selectCartLinesForTheReturn(returnRequest);

      this.partnerService.getPartnerInfo(order.data.lines[0].sku.product.organisation.uuid).subscribe((partner) => {
        returnRequest.organisation = partner;
      });

      if (!this.isCOD(order)) {
        if (order.data.payment_method_charge_id) {
          this.stripeService.getPaymentIntentDetails(order.data.payment_method_charge_id).subscribe((paymentIntent) => {
            returnRequest.paymentIntent = paymentIntent;
          });
        } else {
          this._paymentIntent = {
            charges: {
              data: [
                {
                  payment_method_details: {
                    card: {
                      brand: '',
                      last4: 0
                    }
                  }
                }
              ]
            }
          };
        }
      }

      order.data.lines.findIndex((product) => {
        this.imagesService.getImagesForProduct('SKU', product.sku.uuid).subscribe((image) => {
          if (image.data.length) {
            product.imageSource = image.data[0]?.original.url || '/assets/images/shared/logo.png';
          } else {
            this.imagesService.getImagesForProduct('PRODUCT', product.sku.product.uuid).subscribe((productImage) => {
              product.imageSource = productImage.data[0]?.original.url || '/assets/images/shared/logo.png';
            });
          }
        });
      });
    });
  }

  private selectCartLinesForTheReturn(returnRequest: OrderReturnRequest): void {
    returnRequest.items.forEach((returnItem) => {
      const cartLine = returnRequest.fullOrder?.data.lines.find((line) => line.uuid == returnItem.order_line.uuid);

      if (cartLine) {
        if (!returnRequest.fullOrder?.data.returnedCartLines) {
          returnRequest.fullOrder!.data.returnedCartLines = [];
        }

        returnRequest.fullOrder?.data.returnedCartLines?.push(cartLine);
      }
    });
  }

  private isCOD(order: CreatedOrder): boolean {
    return order.data.paymentMethod.code == PaymentMethod.COD;
  }

  get returns(): OrderReturnRequest[] {
    return this._orderReturnRequests.data.filter(
      (request) => request.fullOrder && request.fullOrder.data.returnedCartLines?.length
    );
  }

  date(date: any): string {
    return new Date(date).toLocaleDateString() || '';
  }

  time(date: any): string {
    return new Date(date).toLocaleTimeString() || '';
  }

  toFixed(input: any): string {
    return Number.parseFloat(input).toFixed(2);
  }

  status(status: any): string {
    return OrderStatus[status];
  }

  goToCategories(): void {
    this.router.navigate(['categories']);
  }

  goToReturnDetails(orderReturnRequest: OrderReturnRequest): void {
    this.router.navigate(['return/' + orderReturnRequest.uuid]);
  }

  goToOrder(order: any): void {
    this.router.navigate(['order/' + order.uuid]);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case this._statuses.cancelled:
        return 'cancel';
      case this._statuses.confirmed:
        return 'success';
      default:
        return '';
    }
  }

  returnOptionValue(option: any): any {
    if (option.option.values.length) {
      const optionSelected = option.option.values.find((value: { uuid: any }) => value.uuid == option.value);
      return optionSelected.value;
    }

    return option.value;
  }

  getUnitPriceBase(cartLine: CartLine): number {
    return Number(cartLine.price_per_piece) || 0;
  }

  getLineTotalBase(
    cartLine: CartLine,
    orderReturnRequest: OrderReturnRequest,
    cartLineUUID: string
  ): number {
    return this.getUnitPriceBase(cartLine) * this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID);
  }

  pricingInfoText(orderReturnRequest: OrderReturnRequest, cartLineUUID: string): string {
    const qty = this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID);
    return `${qty} x`;
  }

  pricingInfo(cartLine: CartLine, orderReturnRequest: OrderReturnRequest, cartLineUUID: string): string {
    const price = cartLine.price_per_piece;
    const priceAsString = Number.parseFloat(price).toFixed(2);
    return this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID) + ' x ' + priceAsString + ' лв.';
  }

  getPriceAsFixedString(cartLine: CartLine, orderReturnRequest: OrderReturnRequest, cartLineUUID: string): string[] | string {
    if (!cartLine.price_per_piece) {
      return '00.00';
    }

    const priceCalculated =
      Number.parseFloat(cartLine.price_per_piece) * this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID);

    const fixedPrice = priceCalculated.toFixed(2);
    const priceArray = fixedPrice.split('.');
    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return priceArray;
  }

  getTotalPriceAsFixedString(price: string): string {
    if (!price) {
      return '00.00';
    }

    const fixedPrice = Number.parseFloat(price).toFixed(2);
    const priceArray = fixedPrice.split('.');
    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return priceArray[0] + '.' + priceArray[1];
  }

  goToProduct(productPerOrg: CartLine): void {
    if (productPerOrg.sku.product.deleted_at) {
      this.toastrService.info('Продуктът вече не е наличен.');
      return;
    }

    this.router.navigate(['product/' + productPerOrg.sku.product.uuid + '/' + 'none']);
  }

  returnStatusTranslation(orderStatus: string): string {
    return ReturnStatus[orderStatus];
  }

  returnReturnQuantityForProduct(orderReturnRequest: OrderReturnRequest, cartLineUUID: string): number {
    const item = orderReturnRequest.items.find((returnItem) => returnItem.order_line.uuid == cartLineUUID);
    return item?.quantity || 0;
  }
}
